import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, cents } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import usePageMeta from "../hooks/usePageMeta";

const TABS = ["overview", "orders", "manifests", "subscriptions", "catalog", "locations", "leads", "readiness", "automation"];

function LocationsPanel() {
  const [locs, setLocs] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/locations").then((r) => setLocs(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put(`/admin/locations/${edit.id}/hours`, edit.fields); toast.success("Hours saved"); setEdit(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-locations-panel">
      {locs.map((l) => (
        <div key={l.id} className="card-paper p-5" data-testid={`admin-location-${l.id}`}>
          <div className="flex flex-wrap justify-between items-start gap-2">
            <div>
              <p className="font-medium">{l.name} <span className="text-xs text-[#171715]/50">({l.status})</span></p>
              <p className="text-sm text-[#171715]/70 mt-1">{l.hours || "No hours recorded"}</p>
              {l.hours_provisional && <span className="inline-block text-[10px] font-mono uppercase text-[#9c3b2a] border border-[#9c3b2a]/40 px-1.5 py-0.5 mt-2" data-testid={`hours-provisional-badge-${l.id}`}>Provisional — pending gym confirmation</span>}
              {l.hours_source && <p className="text-xs text-[#171715]/55 mt-2">Source: <a href={l.hours_source} target="_blank" rel="noreferrer" className="underline">{l.hours_source}</a> · checked {l.hours_checked_on || "—"}</p>}
              {l.hours_source_note && <p className="text-xs text-[#171715]/55 mt-1">{l.hours_source_note}</p>}
              {l.windows_test_only && <p className="text-xs text-[#A35847] mt-1">Windows are TEST-ONLY (derived from provisional gym hours). Production capacity & cutoff unconfirmed.</p>}
            </div>
            <button data-testid={`edit-hours-${l.id}`} className="btn-ghost text-sm" onClick={() => setEdit({ id: l.id, fields: { hours: l.hours || "", hours_source: l.hours_source || "", hours_checked_on: l.hours_checked_on || "", hours_confirmed: !!l.hours_confirmed } })}>Edit hours</button>
          </div>
          {edit?.id === l.id && (
            <div className="mt-4 space-y-2 border-t border-[#EBD9D0] pt-4">
              <input data-testid="hours-edit-text" className="input-line w-full" value={edit.fields.hours} onChange={(e) => setEdit({ ...edit, fields: { ...edit.fields, hours: e.target.value } })} placeholder="Display hours" />
              <input data-testid="hours-edit-source" className="input-line w-full" value={edit.fields.hours_source} onChange={(e) => setEdit({ ...edit, fields: { ...edit.fields, hours_source: e.target.value } })} placeholder="Source URL" />
              <input data-testid="hours-edit-checked" className="input-line w-full" value={edit.fields.hours_checked_on} onChange={(e) => setEdit({ ...edit, fields: { ...edit.fields, hours_checked_on: e.target.value } })} placeholder="Checked on (e.g. 2026-06)" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="hours-edit-confirmed" checked={edit.fields.hours_confirmed} onChange={(e) => setEdit({ ...edit, fields: { ...edit.fields, hours_confirmed: e.target.checked, hours_provisional: !e.target.checked } })} /> Hours confirmed with the gym</label>
              <div className="flex gap-2"><button data-testid="hours-save-btn" className="btn-ink text-sm" onClick={save}>Save</button><button className="btn-ghost text-sm" onClick={() => setEdit(null)}>Cancel</button></div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AnalyticsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  return (
    <div className="card-paper p-5" data-testid="admin-analytics-panel">
      <p className="font-medium text-sm mb-1">Analytics (backend event ledger)</p>
      <p className="text-xs text-[#171715]/60 mb-3">GA4: {data.ga4.status} Consent-gated, cookieless, no PII. {data.revenue_note}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {Object.entries(data.event_counts).map(([ev, n]) => (
          <div key={ev} className="border border-[#EBD9D0] p-2 text-xs"><span className="font-mono block">{ev}</span><span className="text-lg font-mono">{n}</span></div>
        ))}
        {Object.keys(data.event_counts).length === 0 && <p className="text-xs text-[#171715]/60 col-span-4">No events recorded yet (consent banner must be accepted).</p>}
      </div>
    </div>
  );
}

export default function Admin() {
  usePageMeta("Owner Dashboard | Tiff. Wellness Co.", "Operations dashboard.", { robots: "noindex,nofollow" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [metrics, setMetrics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [q, setQ] = useState("");
  const [locations, setLocations] = useState([]);
  const [mLoc, setMLoc] = useState("");
  const [mDate, setMDate] = useState("");
  const [manifest, setManifest] = useState(null);
  const [useBy, setUseBy] = useState("");

  const loadManifest = async (loc = mLoc, date = mDate) => {
    if (!loc || !date) return;
    try { const { data } = await api.get(`/admin/manifest?location_id=${loc}&date=${date}`); setManifest(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const createBatch = async () => {
    try {
      const { data } = await api.post("/admin/batches", { location_id: mLoc, date: mDate, use_by_date: useBy || null });
      toast.success(`Batch saved — ${data.orders_moved_to_production} order(s) moved to production`);
      loadManifest(); loadAll();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const exportManifestCsv = () => {
    const rows = [["juice", "recipe_version", "functional_addition", "bottles"]];
    manifest.bottle_counts.forEach((c) => rows.push([c.name, c.recipe_version, c.functional_addition || "", c.qty]));
    rows.push(["TOTAL", "", "", manifest.total_bottles]);
    rows.push([]);
    rows.push(["order_number", "customer_email", "window", "items", "fulfillment"]);
    manifest.orders.forEach((o) => rows.push([o.order_number, o.email, o.pickup.window_label, o.items.map((i) => `${i.qty}x ${i.name}`).join("; "), o.fulfillment_status]));
    const blob = new Blob([rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `manifest-${mLoc}-${mDate}.csv`; a.click();
  };

  const loadAll = () => {
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/admin/readiness").then((r) => setReadiness(r.data)).catch(() => {});
    api.get("/admin/automation-status").then((r) => setAutomation(r.data)).catch(() => {});
    api.get("/admin/locations").then((r) => setLocations(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (user === false) navigate("/auth?next=/admin");
    if (user && user !== false && user.role !== "admin") navigate("/account");
    if (user && user.role === "admin") loadAll();
  }, [user]); // eslint-disable-line

  const setFulfillment = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/fulfillment`, { fulfillment_status: status }); toast.success("Updated"); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const refund = async (id) => {
    if (!window.confirm("Issue a FULL refund via Stripe? Final status is confirmed by webhook.")) return;
    try { const { data } = await api.post(`/admin/orders/${id}/refund`, {}); toast.success(data.message); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggleAvail = async (slug, available) => {
    try { await api.put(`/admin/products/${slug}`, { available }); toast.success("Saved"); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const exportCsv = () => {
    const rows = [["order_number", "email", "date", "location", "items", "total_cents", "payment", "fulfillment"]];
    orders.forEach((o) => rows.push([o.order_number, o.email, o.pickup?.date, o.pickup?.location_name, o.items.map((i) => `${i.qty}x ${i.name}`).join("; "), o.total_cents, o.payment_status, o.fulfillment_status]));
    const blob = new Blob([rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "orders.csv"; a.click();
  };

  if (!user || user === false || user.role !== "admin") return <div className="py-20 text-center text-sm">Loading…</div>;
  const filteredOrders = orders.filter((o) => !q || (o.email + o.order_number).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <h1 className="text-4xl mb-6">Operations</h1>
      <div className="flex gap-1 flex-wrap mb-8" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
            className={`min-h-[44px] px-4 text-sm capitalize border ${tab === t ? "bg-[#171715] text-[#FBF6F1] border-[#171715]" : "bg-white border-[#EBD9D0]"}`}>{t}</button>
        ))}
      </div>

      {tab === "overview" && metrics && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-kpi-grid">
            {[["Paid sales (collected)", cents(metrics.paid_sales_cents)], ["Refunds", cents(metrics.refunds_cents)], ["Net sales", cents(metrics.net_sales_cents)], ["Active subscriptions", metrics.active_subscriptions], ["MRR (projected)", cents(metrics.mrr_cents)], ["Failed payments", metrics.failed_payments], ["Pickups next 7 days", metrics.pickups_next_7_days], ["Exceptions", metrics.fulfillment_exceptions], ["Leads", metrics.leads], ["Marketing opt-ins", metrics.marketing_opt_ins]].map(([l, v]) => (
              <button key={l} onClick={() => setTab(l.includes("subscription") || l.includes("MRR") ? "subscriptions" : l.includes("Lead") || l.includes("opt-ins") ? "leads" : "orders")} className="card-paper p-4 text-left hover:border-[#A35847]/50" data-testid={`admin-kpi-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="text-xs text-[#171715]/60 block">{l}</span>
                <span className="text-xl font-mono">{v}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-[#171715]/55 mt-3">{metrics.mrr_method}. {metrics.note}</p>
        </div>
      )}

      {tab === "orders" && (
        <div>
          <div className="flex flex-wrap gap-3 mb-4">
            <input data-testid="admin-orders-search" className="input-line max-w-xs" placeholder="Search email / order #" value={q} onChange={(e) => setQ(e.target.value)} />
            <button data-testid="admin-orders-export-csv" className="btn-ghost text-sm" onClick={exportCsv}>Export CSV</button>
          </div>
          <div className="overflow-x-auto card-paper">
            <table className="w-full text-xs min-w-[800px]" data-testid="admin-orders-table">
              <thead><tr className="border-b border-[#EBD9D0] text-left"><th className="p-2">Order</th><th className="p-2">Email</th><th className="p-2">Pickup</th><th className="p-2">Items</th><th className="p-2">Total</th><th className="p-2">Payment</th><th className="p-2">Fulfillment</th><th className="p-2">Actions</th></tr></thead>
              <tbody>
                {filteredOrders.map((o) => (
                  <tr key={o.id} className="border-b border-[#EBD9D0]/60" data-testid={`admin-order-row-${o.order_number}`}>
                    <td className="p-2 font-mono">{o.order_number}<br /><span className="text-[#171715]/50">{o.source}</span></td>
                    <td className="p-2">{o.email}</td>
                    <td className="p-2">{o.pickup?.location_name}<br />{o.pickup?.date}</td>
                    <td className="p-2">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</td>
                    <td className="p-2 font-mono">{cents(o.total_cents)}</td>
                    <td className="p-2 font-mono uppercase">{o.payment_status}{o.refund_pending && "*"}</td>
                    <td className="p-2">
                      <select data-testid={`admin-order-fulfillment-${o.order_number}`} aria-label="fulfillment status" className="input-line !min-h-[36px] !p-1 text-xs" value={o.fulfillment_status} onChange={(e) => setFulfillment(o.id, e.target.value)}>
                        {["pending", "in_production", "ready", "collected", "missed", "canceled", "exception"].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-2">
                      {["paid", "partially_refunded"].includes(o.payment_status) && <button data-testid={`admin-order-refund-${o.order_number}`} className="underline text-[#9c3b2a]" onClick={() => refund(o.id)}>Refund</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#171715]/55 mt-2">* refund initiated, awaiting Stripe webhook confirmation. Payment and fulfillment are separate status fields.</p>
        </div>
      )}

      {tab === "manifests" && (
        <div data-testid="admin-manifests-panel">
          <div className="flex flex-wrap gap-3 mb-5 items-end">
            <div>
              <label className="text-xs font-medium block mb-1" htmlFor="manifest-loc">Location</label>
              <select id="manifest-loc" data-testid="manifest-location-select" className="input-line" value={mLoc} onChange={(e) => setMLoc(e.target.value)}>
                <option value="">Select…</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1" htmlFor="manifest-date">Pickup date</label>
              <input id="manifest-date" data-testid="manifest-date-input" type="date" className="input-line" value={mDate} onChange={(e) => setMDate(e.target.value)} />
            </div>
            <button data-testid="manifest-load-button" className="btn-ink" disabled={!mLoc || !mDate} onClick={() => loadManifest()}>Load manifest</button>
            {manifest && manifest.orders.length > 0 && <button data-testid="manifest-export-csv" className="btn-ghost" onClick={exportManifestCsv}>Export CSV</button>}
          </div>
          {manifest && (
            <div className="space-y-5">
              <div className="card-paper p-4">
                <h3 className="font-medium text-sm mb-3">Bottle counts by recipe — {manifest.total_bottles} total</h3>
                <table className="w-full text-xs" data-testid="manifest-bottle-counts">
                  <thead><tr className="border-b border-[#EBD9D0] text-left"><th className="p-2">Juice</th><th className="p-2">Recipe</th><th className="p-2">Add-in</th><th className="p-2">Bottles</th></tr></thead>
                  <tbody>
                    {manifest.bottle_counts.map((c) => (
                      <tr key={c.slug} className="border-b border-[#EBD9D0]/60 last:border-0">
                        <td className="p-2">{c.name}</td><td className="p-2 font-mono">v{c.recipe_version}</td>
                        <td className="p-2">{c.functional_addition || "—"}</td><td className="p-2 font-mono">{c.qty}</td>
                      </tr>
                    ))}
                    {manifest.bottle_counts.length === 0 && <tr><td className="p-2 text-[#171715]/60" colSpan={4}>No paid orders for this location/date.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="card-paper p-4">
                <h3 className="font-medium text-sm mb-2">Production batch</h3>
                {manifest.batch ? (
                  <p className="text-sm" data-testid="manifest-batch-info">Batch {manifest.batch.id.slice(0, 8)} · status {manifest.batch.status} · use-by {manifest.batch.use_by_date || "not set"}</p>
                ) : <p className="text-sm text-[#171715]/60">No batch yet for this date.</p>}
                <div className="flex flex-wrap gap-2 mt-3 items-end">
                  <div>
                    <label className="text-xs font-medium block mb-1" htmlFor="use-by">Use-by date (applies to bottles regardless of pickup frequency)</label>
                    <input id="use-by" data-testid="manifest-useby-input" type="date" className="input-line" value={useBy} onChange={(e) => setUseBy(e.target.value)} />
                  </div>
                  <button data-testid="manifest-create-batch-button" className="btn-ink" disabled={manifest.orders.length === 0} onClick={createBatch}>
                    {manifest.batch ? "Update batch" : "Create batch & start production"}
                  </button>
                </div>
              </div>
              <div className="card-paper p-4">
                <h3 className="font-medium text-sm mb-3">Orders ({manifest.orders.length})</h3>
                <ul className="text-xs space-y-2" data-testid="manifest-orders-list">
                  {manifest.orders.map((o) => (
                    <li key={o.id} className="flex flex-wrap justify-between gap-2 border-b border-[#EBD9D0]/60 pb-2 last:border-0">
                      <span className="font-mono">{o.order_number}</span>
                      <span>{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</span>
                      <span className="font-mono uppercase text-[#171715]/60">{o.fulfillment_status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "subscriptions" && (
        <div className="overflow-x-auto card-paper">
          <table className="w-full text-xs min-w-[700px]" data-testid="admin-subscriptions-table">
            <thead><tr className="border-b border-[#EBD9D0] text-left"><th className="p-2">Customer</th><th className="p-2">Plan</th><th className="p-2">Location</th><th className="p-2">Status</th><th className="p-2">Next pickup</th><th className="p-2">Next charge</th><th className="p-2">Flavors</th></tr></thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} className="border-b border-[#EBD9D0]/60" data-testid={`admin-sub-row-${s.id}`}>
                  <td className="p-2">{s.email}</td>
                  <td className="p-2">{s.bottles} btl / {s.interval_days}d · {cents(s.price_cents)}</td>
                  <td className="p-2">{s.location_name}</td>
                  <td className="p-2 font-mono uppercase">{s.status}</td>
                  <td className="p-2">{s.next_pickup_date}</td>
                  <td className="p-2">{s.next_charge_at ? new Date(s.next_charge_at).toLocaleDateString() : "—"}</td>
                  <td className="p-2">{s.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "catalog" && (
        <div className="space-y-3" data-testid="admin-catalog-list">
          {products.map((p) => (
            <div key={p.slug} className="card-paper p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium text-sm">{p.name} <span className="font-mono text-xs">{cents(p.price_cents)}</span> <span className="text-xs text-[#171715]/50">recipe v{p.recipe_version}</span></p>
                <p className="text-xs text-[#171715]/60 capitalize">{p.ingredients.join(", ")}{p.functional_addition ? ` + ${p.functional_addition}` : ""}</p>
                <p className="text-[10px] font-mono uppercase text-[#9c3b2a] mt-1">{!p.owner_approved && "owner approval pending"} {!p.nutrition && "· nutrition unknown"} {!p.allergens && "· allergens unknown"}</p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid={`admin-catalog-available-${p.slug}`} type="checkbox" className="h-5 w-5" checked={p.available} onChange={(e) => toggleAvail(p.slug, e.target.checked)} /> Available
              </label>
            </div>
          ))}
          <p className="text-xs text-[#171715]/55">Price changes create versioned records and never silently change an existing subscription's charge. Review flags above are admin-only and kept out of customer-facing copy.</p>
        </div>
      )}

      {tab === "leads" && (
        <div className="overflow-x-auto card-paper">
          <table className="w-full text-xs min-w-[600px]" data-testid="admin-leads-table">
            <thead><tr className="border-b border-[#EBD9D0] text-left"><th className="p-2">Email</th><th className="p-2">Name</th><th className="p-2">Source</th><th className="p-2">Area</th><th className="p-2">Consent</th><th className="p-2">Created</th><th className="p-2"></th></tr></thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-[#EBD9D0]/60">
                  <td className="p-2">{l.email}</td><td className="p-2">{l.name}</td><td className="p-2">{l.source}{l.last_source === "delivery_request" && <span className="block text-[10px] font-mono uppercase text-[#9c3b2a]">delivery request · {(l.delivery_requested_at || "").slice(0, 10)}</span>}</td><td className="p-2">{l.preferred_area}</td>
                  <td className="p-2 font-mono">{l.marketing_opt_in ? (l.unsubscribed ? "unsubscribed" : "opted-in") : "no consent"}</td>
                  <td className="p-2">{l.created_at?.slice(0, 10)}</td>
                  <td className="p-2"><button data-testid={`admin-lead-delete-${l.id}`} className="underline text-[#9c3b2a]" onClick={async () => { if (window.confirm("Remove this lead record?")) { await api.delete(`/admin/leads/${l.id}`); loadAll(); } }}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "readiness" && readiness && (
        <div data-testid="admin-readiness-panel">
          <p className={`card-paper p-4 text-sm font-medium mb-4 ${readiness.live_checkout_blocked ? "text-[#9c3b2a]" : "text-[#A35847]"}`} data-testid="admin-readiness-status">
            {readiness.live_checkout_blocked ? "Live checkout is BLOCKED — missing operating facts below. The app runs in a clearly labeled test environment." : "Ready for launch review."}
          </p>
          {readiness.activation_dependencies && (
            <div className="card-paper p-4 mb-4" data-testid="admin-activation-dependencies">
              <p className="font-medium text-sm mb-2">Launch activation dependencies</p>
              <ul className="space-y-2 text-xs">
                {readiness.activation_dependencies.map((d, i) => (
                  <li key={i}><span className="font-mono uppercase text-[#A35847]">{d.name}</span> — <span className="font-medium">{d.status}</span>: <span className="text-[#171715]/70">{d.detail}</span></li>
                ))}
              </ul>
            </div>
          )}
          <ul className="space-y-2">
            {readiness.items.map((i, idx) => (
              <li key={idx} className="card-paper p-3 text-sm flex flex-wrap justify-between gap-2" data-testid={`readiness-item-${idx}`}>
                <span><span className="font-mono text-xs uppercase text-[#171715]/50 mr-2">{i.area}</span><span className="font-medium">{i.name}</span> — {i.missing.join(", ")}</span>
                {i.blocking_live_checkout && <span className="text-[10px] font-mono uppercase text-[#9c3b2a] border border-[#9c3b2a]/40 px-1.5 py-0.5 self-start">blocks live</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "locations" && <LocationsPanel />}

      {tab === "automation" && automation && (
        <div data-testid="admin-automation-panel" className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {["resend", "n8n", "sms"].map((k) => (
              <div key={k} className="card-paper p-5">
                <p className="font-medium capitalize">{k}</p>
                <p data-testid={`automation-status-${k}`} className={`font-mono text-sm mt-1 ${automation[k].connected ? "text-[#A35847]" : "text-[#9c3b2a]"}`}>{automation[k].status}</p>
                {automation[k].last_success && <p className="text-xs text-[#171715]/60 mt-1">Last success: {automation[k].last_success}</p>}
                {automation[k].failed_jobs != null && <p className="text-xs text-[#171715]/60">Failed jobs: {automation[k].failed_jobs} · Queued: {automation[k].queued_jobs}</p>}
                {automation[k].next_run && <p className="text-xs text-[#171715]/60">{automation[k].next_run}</p>}
                <p className="text-xs text-[#171715]/65 mt-2">{automation[k].setup}</p>
                {k === "n8n" && (
                  <p className="text-xs text-[#A35847] font-medium mt-2" data-testid="n8n-optional-note">
                    n8n is optional — all emails already run via the built-in scheduler (cron). It is NOT a launch blocker.
                  </p>
                )}
                {k === "sms" && !automation.sms?.connected && (
                  <p className="text-xs text-[#9c3b2a] font-medium mt-2" data-testid="sms-simulated-note">
                    SIMULATED — no texts are actually sent. Paste your Twilio keys into backend/.env to go live.
                  </p>
                )}
              </div>
            ))}
          </div>
          <div className="card-paper p-5">
            <p className="font-medium text-sm mb-2">Email queue executor: <span className="font-mono">{automation.executor}</span></p>
            <p className="text-xs text-[#171715]/60 mb-3">Exactly one executor dispatches queued jobs. Cron reclaims automatically if n8n is silent for 2 hours.</p>
            <div className="flex gap-2">
              {["cron", "n8n"].map((ex) => (
                <button key={ex} data-testid={`executor-set-${ex}`} className={automation.executor === ex ? "btn-ink text-sm" : "btn-ghost text-sm"}
                  onClick={async () => { try { await api.put("/admin/email-executor", { executor: ex }); toast.success(`Executor: ${ex}`); loadAll(); } catch (e) { toast.error(errMsg(e)); } }}>
                  {ex}
                </button>
              ))}
            </div>
          </div>
          <div className="card-paper p-5" data-testid="admin-download-center">
            <p className="font-medium text-sm mb-2">Download center — n8n workflow</p>
            <p className="text-xs text-[#171715]/60 mb-3">Original workflow using only built-in n8n nodes (Schedule/HTTP/IF/Split/Error Trigger); pattern adapted from the public n8n abandoned-cart template by Yaron Been (no code copied). Import into n8n is unverified until you import it once.</p>
            <p className="text-xs text-[#171715]/60 mb-3" data-testid="n8n-plan-budget-note">Cloud plan note: at the exported 15-minute polling interval the schedule runs 2,976 executions in a 31-day month — over the 2,500/month n8n Cloud Starter budget. On Starter, change the Schedule Trigger to every 30 minutes (1,488/month); for 15-minute polling use Pro or self-host. Verify current n8n pricing yourself before subscribing.</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="download-n8n-workflow" className="btn-ghost text-sm" onClick={async () => {
                const { data } = await api.get("/admin/downloads/n8n-workflow");
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "tiff-email-queue-executor.json"; a.click();
              }}>Workflow JSON</button>
              <button data-testid="download-n8n-error-workflow" className="btn-ghost text-sm" onClick={async () => {
                const { data } = await api.get("/admin/downloads/n8n-error-workflow");
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "tiff-email-queue-error-handler.json"; a.click();
              }}>Error-handler JSON</button>
              <button data-testid="download-n8n-guide" className="btn-ghost text-sm" onClick={async () => {
                const { data } = await api.get("/admin/downloads/n8n-guide");
                const blob = new Blob([data.content], { type: "text/markdown" });
                const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = data.filename; a.click();
              }}>Setup guide</button>
            </div>
          </div>
          <div className="card-paper p-5" data-testid="admin-editorial-downloads">
            <p className="font-medium text-sm mb-2">Download center — journal & SEO</p>
            <p className="text-xs text-[#171715]/60 mb-3">Content export, SEO manifest, keyword strategy (owner-only, never shown in articles) and the tiff-wellness.com domain migration guide.</p>
            <div className="flex flex-wrap gap-2">
              {[["content-export", "Content export (JSON)"], ["seo-manifest", "SEO manifest (CSV)"], ["seo-strategy", "SEO strategy"], ["migration-guide", "Domain migration guide"]].map(([k, label]) => (
                <button key={k} data-testid={`download-${k}`} className="btn-ghost text-sm" onClick={async () => {
                  try {
                    const { data } = await api.get(`/admin/downloads/${k}`);
                    const blob = new Blob([data.content], { type: "text/plain" });
                    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = data.filename; a.click();
                  } catch (e) { toast.error(errMsg(e)); }
                }}>{label}</button>
              ))}
            </div>
          </div>
          <AnalyticsPanel />
        </div>
      )}
    </div>
  );
}
